// @ts-nocheck
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { formatContentDuration } from "@/lib/utils";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  GraduationCap,
  BookOpen,
  CheckCircle,
  Lock,
  Clock,
  Award,
  PlayCircle,
  TrendingUp,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/courses")({
  beforeLoad: () => {
    throw redirect({ to: "/training" });
  },
  component: CoursesPage,
});

import { STUDENT_QUERY_OPTIONS, STUDENT_QUERY_KEYS, invalidateAllStudentState } from "@/lib/api";
import type { Course, Enrollment } from "@/lib/types";

function CoursesPage() {
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState<"enrolled" | "available">("enrolled");
  const { user } = useAuth();

  // Fetch enrolled courses
  const { data: enrollments = [], isLoading: enrollmentsLoading } = useQuery<Enrollment[]>({
    queryKey: STUDENT_QUERY_KEYS.myCourseEnrollments(user?.id),
    queryFn: () => [],
    enabled: false,
    ...STUDENT_QUERY_OPTIONS,
  });

  // Fetch available (published) courses, filtering out already enrolled ones
  const { data: availableCourses = [], isLoading: coursesLoading } = useQuery<Course[]>({
    queryKey: STUDENT_QUERY_KEYS.availableCourses(user?.id),
    queryFn: async () => [],
    enabled: false,
    ...STUDENT_QUERY_OPTIONS,
  });

  const enrollCourse = useMutation({
    mutationFn: async (courseId: string) => {},
    onSuccess: () => {
      invalidateAllStudentState(qc, user?.id);
      toast.success("Successfully enrolled in course!");
    },
    onError: (error: Error) => {
      toast.error(`Failed to enroll: ${error.message}`);
    },
  });

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
          <GraduationCap className="h-8 w-8" />
          My Courses
        </h1>
        <p className="text-muted-foreground mt-2">
          Complete courses to advance your training and unlock certificates
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={(v: any) => setActiveTab(v)}>
        <TabsList>
          <TabsTrigger value="enrolled" className="gap-2">
            <BookOpen className="h-4 w-4" />
            My Enrolled Courses ({enrollments.length})
          </TabsTrigger>
          <TabsTrigger value="available" className="gap-2">
            <TrendingUp className="h-4 w-4" />
            Available Courses
          </TabsTrigger>
        </TabsList>

        <TabsContent value="enrolled" className="mt-6">
          {enrollmentsLoading ? (
            <div className="text-center py-12 text-muted-foreground">Loading courses...</div>
          ) : enrollments.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center space-y-4">
                <GraduationCap className="h-16 w-16 mx-auto text-muted-foreground" />
                <div>
                  <h3 className="text-lg font-semibold">No courses enrolled yet</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Browse available courses to get started
                  </p>
                </div>
                <Button onClick={() => setActiveTab("available")}>Explore Courses</Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {enrollments.map((enrollment) => {
                const course = enrollment.course;
                const isCompleted = enrollment.status === "completed";
                const isInProgress = enrollment.status === "in_progress";

                return (
                  <Card
                    key={enrollment.id}
                    className="flex flex-col hover:shadow-lg transition-shadow"
                  >
                    <CardHeader>
                      <div className="flex justify-between items-start mb-2">
                        <CardTitle className="text-lg">{course.title}</CardTitle>
                        {isCompleted && <CheckCircle className="h-5 w-5 text-green-500 shrink-0" />}
                      </div>
                      {course.category && (
                        <Badge variant="outline" className="w-fit">
                          {course.category}
                        </Badge>
                      )}
                      <CardDescription className="line-clamp-2 mt-2">
                        {course.description || "No description available"}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="mt-auto space-y-4">
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <BookOpen className="h-4 w-4" />
                          <span>{course.modules?.length ?? 0} modules</span>
                        </div>
                        {course.total_duration_seconds > 0 && (
                          <div className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            <span>{formatContentDuration(course.total_duration_seconds)}</span>
                          </div>
                        )}
                      </div>

                      <div className="space-y-2">
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>Progress</span>
                          <span>{enrollment.completion_percentage}%</span>
                        </div>
                        <Progress value={enrollment.completion_percentage} className="h-2" />
                      </div>

                      <div className="flex gap-2">
                        <Button
                          asChild
                          variant={isCompleted ? "outline" : "default"}
                          className="flex-1"
                        >
                          <Link to="/training">
                            {isCompleted ? "Review" : isInProgress ? "Continue" : "Start"}
                          </Link>
                        </Button>
                        {isCompleted && course.exam && (
                          <Button asChild variant="outline" className="gap-2">
                            <Link to="/exams">
                              <Award className="h-4 w-4" />
                              Exam
                            </Link>
                          </Button>
                        )}
                      </div>

                      {enrollment.completed_at && (
                        <div className="text-xs text-muted-foreground">
                          Completed on {new Date(enrollment.completed_at).toLocaleDateString()}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="available" className="mt-6">
          {coursesLoading ? (
            <div className="text-center py-12 text-muted-foreground">Loading courses...</div>
          ) : availableCourses.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <BookOpen className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold">No courses available</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  You are enrolled in all published courses
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {availableCourses.map((course) => (
                <Card key={course.id} className="flex flex-col hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <CardTitle className="text-lg">{course.title}</CardTitle>
                    {course.category && (
                      <Badge variant="outline" className="w-fit">
                        {course.category}
                      </Badge>
                    )}
                    <CardDescription className="line-clamp-3 mt-2">
                      {course.description || "No description available"}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="mt-auto space-y-4">
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <BookOpen className="h-4 w-4" />
                        <span>{course.modules?.length ?? 0} modules</span>
                      </div>
                      {course.total_duration_seconds > 0 && (
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          <span>{formatContentDuration(course.total_duration_seconds)}</span>
                        </div>
                      )}
                    </div>

                    {course.exam && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Award className="h-4 w-4 text-amber-500" />
                        <span>Includes final exam</span>
                      </div>
                    )}

                    <Button
                      className="w-full"
                      onClick={() => enrollCourse.mutate(course.id)}
                      disabled={enrollCourse.isPending}
                    >
                      {enrollCourse.isPending ? "Enrolling..." : "Enroll Now"}
                      <PlayCircle className="ml-2 h-4 w-4" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
